// The shader prop registry: every prop of every shader, so a binding, a preset fade and any prop list read the same on every shader. Node-safe data.

// Panel sections in order, `primary` heading the collapsed one; a `loose` group's params are rows, an `after` section sits among them.
export const GROUPS = [
	{ id: 'motion', label: 'Motion', primary: [] },
	{ id: 'form', label: 'Form', primary: [], loose: true },
	{ id: 'width', label: 'Width', primary: ['width'], after: 'count' },
	// `titled`: collapsed, the section keeps its own name, not its headline's.
	{ id: 'wave_b', label: '2nd Wave', primary: ['wave_b_height'], titled: true, after: 'wave_speed' },
	{ id: 'wave_c', label: '3rd Wave', primary: ['wave_c_height'], titled: true, after: 'wave_speed' },
	{ id: 'color', label: 'Color', primary: ['color', 'color_b', 'ground'] },
	{ id: 'camera', label: 'Camera', primary: ['speed'], titled: true },
]

// Declaration order is the one sort order; the other flags are `checkModule`'s rules. `drag` is the stage gesture riding the id (`hold`, `scrub`, `turn`; none when absent; a module's `expose` may override it). `stage` is the pipeline stage the id feeds (camera, surface, path, lattice, displacement, relief, profile, light, palette); an id without one is outside the pipeline.
export const VOCAB = {
	speed: {
		group: 'camera',
		stage: 'lattice',
		label: 'Speed',
		unit: '×',
		curve: 'cubic',
		minimum: -16,
		maximum: 16,
		drag: 'scrub',
		clock: true,
		base: true,
		capped: true,
		temporal: true,
	},
	wobble_view: { group: 'motion', stage: 'camera', label: 'Wobble View', minimum: 0, maximum: 1, step: 0.01 },
	wobble_rate: {
		group: 'motion',
		stage: 'camera',
		label: 'Wobble Rate',
		unit: '×',
		curve: 'cubic',
		minimum: 0,
		maximum: 8,
		initial: 1,
		flags: { integrate: true },
		base: true,
		temporal: true,
	},
	temperature: {
		group: 'motion',
		stage: 'displacement',
		label: 'Temperature',
		unit: '×',
		curve: 'cubic',
		minimum: -8,
		maximum: 8,
		step: 0.01,
		flags: { integrate: true },
		base: true,
		temporal: true,
		drag: 'scrub',
	},

	// Sizes, then bends of the geometry, a tunnel's, the morph round the outline, the blobs and the rows.
	// Whole at rest, its top stop "all": as many lines as cover the frame along the flow (lattice's countLines). A path carries it fractional, the next line growing in.
	count: {
		group: 'form',
		stage: 'lattice',
		label: 'Count',
		minimum: 1,
		maximum: 30,
		step: 1,
		flags: { formatter: 'all' },
	},
	width: {
		group: 'width',
		stage: 'lattice',
		label: 'Width',
		unit: 'su',
		curve: 'power',
		spatial: true,
		drag: 'hold',
	},
	grow: { group: 'width', stage: 'lattice', label: 'Grow', minimum: -1, maximum: 1, step: 0.01 },
	fill: { group: 'width', stage: 'light', label: 'Fill', minimum: 0, maximum: 1, step: 0.01 },
	spacing: {
		group: 'form',
		stage: 'lattice',
		label: 'Spacing',
		unit: 'su',
		curve: 'power',
		spatial: true,
		drag: 'hold',
	},
	// Width's share of the pitch: spacing + gap · width. Path-only: ribbons 0, the plane 1.
	gap: { group: 'form', stage: 'light', label: 'Gap', minimum: 0, maximum: 1 },
	spacing_y: {
		group: 'form',
		label: 'Spacing Y',
		unit: 'su',
		minimum: 0,
		maximum: 100,
		step: 0.1,
		curve: 'power',
		spatial: true,
		drag: 'hold',
	},
	cross: { group: 'form', stage: 'lattice', label: 'Cross', minimum: 0, maximum: 1, step: 0.01 },
	twist: { group: 'form', stage: 'lattice', label: 'Twist' },
	unwind: { group: 'form', stage: 'lattice', label: 'Unwind', minimum: 0, maximum: 1, step: 0.01 },
	// A tunnel's path: how strongly it curves, which way round the tunnel, the swing of that curve and the turn of its plane down the depth.
	bend: { group: 'form', stage: 'path', label: 'Bend', minimum: 0, maximum: 3, step: 0.05 },
	bend_angle: {
		group: 'form',
		stage: 'path',
		label: 'Bend Angle',
		unit: '°',
		minimum: 0,
		maximum: 360,
		step: 1,
		flags: { wrap: true },
	},
	wander: {
		group: 'form',
		stage: 'path',
		label: 'Wander',
		minimum: 0,
		maximum: 4,
		step: 0.01,
		flags: { integrate: true },
	},
	bend_roll: {
		group: 'form',
		stage: 'path',
		label: 'Bend Roll',
		minimum: -2,
		maximum: 2,
		step: 0.01,
		flags: { integrate: true },
	},
	radius: { group: 'form', stage: 'surface', label: 'Radius', minimum: 2.5, maximum: 9, step: 0.1 },
	tunnel: { group: 'form', stage: 'surface', label: 'Tunnel', minimum: 0, maximum: 1, step: 0.01 },
	fold: { group: 'form', stage: 'surface', label: 'Fold', minimum: 0, maximum: 1 },
	fit: { group: 'form', stage: 'surface', label: 'Fit', minimum: 0, maximum: 1 },
	rounding: { group: 'form', stage: 'surface', label: 'Rounding', minimum: 0, maximum: 1, step: 0.01 },
	relief: { group: 'form', stage: 'relief', label: 'Relief', minimum: 0, maximum: 1, step: 0.01 },
	cube_size: { group: 'form', stage: 'relief', label: 'Cube Size', unit: 'su', minimum: 10, maximum: 80, step: 1 },
	cube_levels: { group: 'form', stage: 'relief', label: 'Cube Levels', minimum: 1, maximum: 4, step: 1 },
	cube_fill: { group: 'form', stage: 'relief', label: 'Cube Fill', minimum: 0, maximum: 1, step: 0.01 },
	cube_split: { group: 'form', stage: 'relief', label: 'Cube Split', minimum: 0, maximum: 1, step: 0.01 },
	cube_split_fill: { group: 'form', stage: 'relief', label: 'Split Fill', minimum: 0, maximum: 1, step: 0.01 },
	cube_corner: { group: 'form', stage: 'relief', label: 'Corner Bias', minimum: 0, maximum: 1, step: 0.01 },
	cube_cluster: { group: 'form', stage: 'relief', label: 'Clustering', minimum: 0, maximum: 1, step: 0.01 },
	cube_cluster_size: { group: 'form', stage: 'relief', label: 'Cluster Size', minimum: 1, maximum: 32, step: 1 },
	cube_seed: { group: 'form', stage: 'relief', label: 'Seed', minimum: 0, maximum: 20, step: 1 },
	blobs: { group: 'form', stage: 'displacement', label: 'Blobs', minimum: 1, maximum: 6, step: 0.05 },
	stretch: { group: 'form', stage: 'displacement', label: 'Stretch', minimum: 0, maximum: 1, step: 0.01 },
	blob_size: {
		group: 'form',
		stage: 'displacement',
		label: 'Blob Size',
		unit: 'su',
		curve: 'power',
		minimum: 10,
		maximum: 160,
		step: 1,
		spatial: true,
	},
	blob_soft: {
		group: 'form',
		stage: 'displacement',
		label: 'Blob Softness',
		unit: 'su',
		minimum: 0,
		maximum: 200,
		step: 1,
		spatial: true,
	},
	hill_rows: {
		group: 'form',
		stage: 'displacement',
		label: 'Hills',
		curve: 'log',
		minimum: 0.01,
		maximum: 1,
		step: 0.005,
	},
	shape: { group: 'form', stage: 'displacement', label: 'Shape' },
	rows: { group: 'form', stage: 'light', label: 'Rows', minimum: 0, maximum: 1, step: 1 },
	wave_length: {
		group: 'form',
		stage: 'displacement',
		label: 'Wave Length',
		unit: 'su',
		curve: 'log',
		step: 1,
		spatial: true,
		drag: 'hold',
	},
	wave_height: {
		group: 'form',
		stage: 'displacement',
		label: 'Wave Height',
		unit: 'su',
		curve: 'power',
		minimum: 0,
		step: 0.1,
		spatial: true,
	},
	wave_direction: {
		group: 'form',
		stage: 'displacement',
		label: 'Wave Direction',
		unit: '°',
		minimum: 0,
		maximum: 360,
		step: 1,
		flags: { wrap: true },
	},
	wave_speed: { group: 'form', stage: 'displacement', label: 'Wave Speed', unit: 'su/s', curve: 'cubic', step: 1 },
	enclose: { group: 'form', stage: 'surface', label: 'Enclose', minimum: 0, maximum: 1 },
	walls: { group: 'form', stage: 'light', label: 'Walls', minimum: 0, maximum: 1, step: 0.01 },

	// The 2nd and 3rd waves, their speeds multiples of `wave_speed`. The first (`wave_length`, `wave_height`) is also a 1D pattern's wave along its bands.
	...Object.fromEntries(
		['b', 'c'].flatMap(w => [
			[
				`wave_${w}_length`,
				{
					group: `wave_${w}`,
					stage: 'displacement',
					label: 'Length',
					unit: 'su',
					curve: 'log',
					step: 1,
					spatial: true,
				},
			],
			[
				`wave_${w}_height`,
				{
					group: `wave_${w}`,
					stage: 'displacement',
					label: 'Height',
					unit: 'su',
					curve: 'power',
					minimum: 0,
					step: 0.1,
					spatial: true,
				},
			],
			[`wave_${w}_speed`, { group: `wave_${w}`, stage: 'displacement', label: 'Speed', unit: '×', step: 0.01 }],
		])
	),

	// The palette, then the light on it.
	color: { group: 'color', stage: 'palette', label: 'Color', flags: { kind: 'color' } },
	color_b: { group: 'color', stage: 'palette', flags: { kind: 'color' } },
	// The surface between and behind the lines, alpha included; outside the palette's colour count.
	ground: { group: 'color', stage: 'palette', label: 'Ground', flags: { kind: 'color', alpha: true } },
	spread: {
		group: 'color',
		stage: 'palette',
		label: 'Spread',
		minimum: 2,
		maximum: 16,
		step: 1,
	},
	repeat: {
		group: 'color',
		stage: 'palette',
		label: 'Repeat',
		minimum: 0,
		maximum: 1,
		step: 1,
		flags: { kind: 'select', enum: [0, 1], enumLabels: ['Ramp', 'Mirror'] },
	},
	wash: {
		group: 'color',
		stage: 'palette',
		label: 'Wash',
		minimum: -8,
		maximum: 8,
		step: 0.05,
		// `settle`: zeroing Wash coasts to a whole colour rather than stranding the bands mid-fade.
		flags: { integrate: true, settle: 1, cycle: 1, carry: true },
		temporal: true,
	},
	light: { group: 'color', stage: 'light', label: 'Light', minimum: 0, maximum: 1, step: 0.01 },
	light_angle: {
		group: 'color',
		stage: 'light',
		label: 'Light Angle',
		unit: '°',
		minimum: 0,
		maximum: 360,
		step: 1,
		flags: { wrap: true },
	},
	fog: { group: 'color', stage: 'light', label: 'Fog', minimum: 0 },
	rings: { group: 'color', stage: 'light', label: 'Rings', minimum: 0, maximum: 3, step: 0.05 },
	ring_speed: {
		group: 'color',
		stage: 'light',
		label: 'Ring Speed',
		minimum: -8,
		maximum: 8,
		step: 0.05,
		flags: { integrate: true },
		temporal: true,
	},

	// Where the picture is seen from.
	zoom: {
		group: 'camera',
		stage: 'camera',
		label: 'Zoom',
		unit: '×',
		curve: 'log',
		initial: 1,
		step: 0.01,
		drag: 'hold',
	},
	angle: {
		group: 'camera',
		stage: 'lattice',
		label: 'Angle',
		unit: '°',
		minimum: 0,
		maximum: 360,
		step: 1,
		flags: { wrap: true },
		drag: 'turn',
	},
	roll: {
		group: 'camera',
		stage: 'camera',
		label: 'Roll',
		unit: '°',
		minimum: 0,
		maximum: 360,
		step: 1,
		flags: { wrap: true },
		drag: 'turn',
	},
	perspective: { group: 'camera', stage: 'camera', label: 'Perspective', minimum: 0, step: 0.01 },
	tilt: { group: 'camera', stage: 'camera', label: 'Tilt', unit: '°', minimum: 0, maximum: 90 },
	elevation: { group: 'camera', stage: 'camera', label: 'Elevation', unit: 'su', spatial: true },
	start: { group: 'camera', stage: 'light', label: 'Start', unit: 'su', minimum: 0, maximum: 400, step: 1 },
	parallax: { group: 'camera', stage: 'light', label: 'Parallax', unit: '×', minimum: 1, maximum: 2, step: 0.01 },
	// 2D, the camera's place on the plane: its shape is the pipeline's (`PARAMS.pan.glsl`); the range bounds each axis.
	pan: {
		group: 'camera',
		stage: 'camera',
		label: 'Pan',
		unit: 'su',
		minimum: -1000,
		maximum: 1000,
		step: 0.1,
		spatial: true,
		drag: 'hold',
	},
}

/** Every shader carries these; `zoom` too once it has a `spatial` param. */
export const REQUIRED = ['speed', 'color', 'color_b']

/** Keys collapsed into a registry id. `depth` reads inverted as `fog`. Retired ids collapse into their successors, so a stored value reads through. */
export const SYNONYMS = {
	thickness: 'width',
	line: 'width',
	size: 'zoom',
	cell: 'zoom',
	framing: 'zoom',
	yaw: 'angle',
	swell_length: 'wave_length',
	arms: 'count',
	wobble_freq: 'wobble_rate',
	jitter: 'wobble_rate',
	wind: 'bend',
	depth: 'fog',
	bg_color: 'color_b',
	glow_color: 'color_b',
	shade_color: 'color_b',
}

const RANK = Object.fromEntries(Object.keys(VOCAB).map((key, i) => [key, i]))

/** A key's place in the one sort order: its own, or its synonym's; unregistered keys sort last. */
export const rankOf = key => RANK[key] ?? RANK[SYNONYMS[key]] ?? Infinity

/** A prop's display group; an unregistered key falls in `form`. */
export const groupOf = key => VOCAB[key]?.group ?? VOCAB[SYNONYMS[key]]?.group ?? 'form'

/** `[key, def]` entries in registry order, stable for unregistered keys. */
export const sortParams = entries => [...entries].sort(([a], [b]) => rankOf(a) - rankOf(b))

/** The keys heading a group's collapsed section: every `primary` the shader has, else the group's first prop. */
const primariesOf = (group, keys) => {
	const own = GROUPS.find(g => g.id === group).primary.filter(k => keys.includes(k))
	return own.length ? own : keys.slice(0, 1)
}

/** `[key, def]` entries as the panel's items in order: a section (`params`, `headlines`) or a loose `{ param }`, an `after` section ranked just past its anchor. */
export const groupParams = entries => {
	const sections = GROUPS.map(({ id, label, titled, loose, after }) => {
		const params = sortParams(entries.filter(([k]) => groupOf(k) === id))
		return {
			id,
			label,
			titled,
			loose,
			after,
			params,
			headlines: primariesOf(
				id,
				params.map(([k]) => k)
			),
		}
	})
	const placed = sections.filter(s => s.after && s.params.length)
	return sections.flatMap(({ loose, after, ...s }) => {
		if (after) return []
		if (!loose) return s.params.length ? [s] : []
		const items = [
			...s.params.map(param => ({ param, rank: rankOf(param[0]) })),
			...placed.map(({ loose: _, after: anchor, ...p }) => ({ ...p, rank: rankOf(anchor) + 0.5 })),
		]
		return items.sort((a, b) => a.rank - b.rank).map(({ rank: _, ...item }) => item)
	})
}
